/**
 * Bounties — cross-Clan scrollable feed of active bounties.
 *
 * Route: /bounties. Every active bounty across the platform lives here,
 * filterable by All / Ending soon. Tapping a card opens the bounty's
 * Clan feed on the Bounties tab where the fan can submit proof.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/AppShell';
import api from '../api';

const FILTER_KEYS = ['all', 'ending_soon'];

const fmtRelDate = (iso, t) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((d.getTime() - Date.now()) / 1000);
  if (s < 0) return t('bounties.timeEnded');
  const m = Math.floor(s / 60);
  if (m < 60) return t('bounties.timeMinsLeft', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('bounties.timeHoursLeft', { count: h });
  const dd = Math.floor(h / 24);
  return t('bounties.timeDaysLeft', { count: dd });
};

export default function Bounties() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (f) => {
    setItems(null);
    try {
      const { data } = await api.get('/bounties/feed', { params: { filter: f, limit: 50 } });
      setItems(data.bounties || []);
      setHasMore(!!data.has_more);
    } catch (ex) {
      setItems([]);
      setHasMore(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get('/bounties/feed', {
        params: { filter, limit: 50, skip: items.length },
      });
      setItems((prev) => [...prev, ...(data.bounties || [])]);
      setHasMore(!!data.has_more);
    } finally {
      setLoadingMore(false);
    }
  };

  const filteredItems = React.useMemo(() => {
    if (!items) return items;
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((b) => (
      (b.title || '').toLowerCase().includes(term) ||
      (b.description || '').toLowerCase().includes(term) ||
      (b.creator_handle || '').toLowerCase().includes(term) ||
      (b.creator_display_name || '').toLowerCase().includes(term)
    ));
  }, [items, search]);

  const filterLabel = (k) => k === 'all' ? t('bounties.filterAll') : t('bounties.filterEndingSoon');

  return (
    <AppShell activeTab="bounties">
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }} data-testid="bounties-page">
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 4, fontFamily: "'Baloo 2', sans-serif" }}>
            {t('bounties.title')}
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>
            {t('bounties.subtitle')}
          </p>
        </div>

        {/* Search + filter row */}
        <div className="discovery-toolbar">
          <div className="discovery-search">
            <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
              <path d="M14 14l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder={t('bounties.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="bounties-search"
            />
          </div>
          <div className="discovery-filters">
            {FILTER_KEYS.map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`discovery-chip${filter === k ? ' active' : ''}`}
                data-testid={`bounties-filter-${k}`}
              >
                {filterLabel(k)}
              </button>
            ))}
          </div>
        </div>

        {items === null && (
          <div style={{ padding: '60px 0', textAlign: 'center', opacity: 0.7 }} data-testid="bounties-loading">
            {t('bounties.loading')}
          </div>
        )}

        {items && filteredItems.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center', opacity: 0.6 }} data-testid="bounties-empty">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
              {search.trim()
                ? t('bounties.noMatchesTitle', { term: search.trim() })
                : t('bounties.emptyTitle')}
            </div>
            <div style={{ fontSize: 13 }}>
              {search.trim() ? t('bounties.noMatchesBody') : t('bounties.emptyBody')}
            </div>
          </div>
        )}

        {items && filteredItems.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {filteredItems.map((b) => (
              <BountyFeedCard
                key={b.id}
                bounty={b}
                onOpen={() => navigate(`/clan/${b.clan_handle}/feed?tab=bounties`, { state: { from: '/bounties' } })}
              />
            ))}
          </div>
        )}

        {hasMore && !search.trim() && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              className="mc-btn-secondary"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="bounties-load-more"
            >
              {loadingMore ? t('bounties.loadMoreBusy') : t('bounties.loadMore')}
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}


function BountyFeedCard({ bounty, onOpen }) {
  const { t } = useTranslation();
  const slotsLeft = Math.max(0, bounty.winner_count - bounty.winners_selected);
  const filledPct = bounty.winner_count ? Math.round((bounty.winners_selected / bounty.winner_count) * 100) : 0;
  const creatorInitials = (bounty.creator_display_name || bounty.creator_handle || 'C')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const gradient = bounty.creator_gradient
    ? `linear-gradient(135deg, ${bounty.creator_gradient[0]}, ${bounty.creator_gradient[1]})`
    : 'linear-gradient(135deg, #00B4FF, #B584F5)';

  return (
    <button
      onClick={onOpen}
      style={{
        padding: 16, borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(0,180,255,0.06), rgba(181,132,245,0.06))',
        border: '1px solid rgba(0,180,255,0.2)',
        textAlign: 'left', cursor: 'pointer',
        color: 'inherit', fontFamily: 'inherit',
        transition: 'transform 0.15s, border 0.15s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      data-testid={`bounties-card-${bounty.id}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {bounty.creator_avatar ? (
          <img src={bounty.creator_avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, color: '#fff',
          }}>{creatorInitials}</div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {bounty.creator_display_name || bounty.creator_handle}
          </div>
          <div style={{ fontSize: 11, opacity: 0.65 }}>@{bounty.creator_handle}</div>
        </div>
        {bounty.ends_at && (
          <span style={{
            padding: '3px 8px', fontSize: 10, borderRadius: 4,
            background: 'rgba(255,183,77,0.15)', color: '#FFB74D', fontWeight: 700,
          }}>{fmtRelDate(bounty.ends_at, t)}</span>
        )}
      </div>

      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{bounty.title}</div>
      {bounty.description && (
        <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.4, marginBottom: 10,
                       display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                       overflow: 'hidden' }}>
          {bounty.description}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, background: 'rgba(180,255,0,0.15)', color: '#B4FF00', fontWeight: 700 }}>
          {t('bounties.rewardPerWinner', { amount: `${Number(bounty.reward_per_winner_pts || 0).toFixed(2)} ${t('common.pts')}` })}
        </span>
        <span style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, background: 'rgba(255,255,255,0.06)', fontWeight: 600 }}>
          {t('bounties.slots', { left: slotsLeft, total: bounty.winner_count })}
        </span>
        {bounty.min_bits_to_join > 1 && (
          <span style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, background: 'rgba(181,132,245,0.15)', color: '#B584F5', fontWeight: 600 }}>
            {t('bounties.minBitsToEnter', { count: bounty.min_bits_to_join })}
          </span>
        )}
      </div>

      <div style={{ height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${filledPct}%`, height: '100%', background: 'linear-gradient(90deg, #00B4FF, #B584F5)' }} />
      </div>
    </button>
  );
}
